/**
 * 买赠自动加购/移除（低 fetch + Metaobjects 多规则）
 * 配置来源：
 * - gift_with_purchase（买 A 送 B，type: buy_x_get_y）
 * - gift_with_threshold（满低消送 C，type: minimum_spend）
 *
 * 自动加购行带隐藏属性 _gift_auto_add / _gift_rule_id / _gift_type；
 * 客户付费单独购买同产品（无属性）不会被同步逻辑改动。
 */
(function () {
  const GIFT_SOURCE = "gift-auto-add";
  const DAILY_INIT_STORAGE_KEY = "gift_auto_add_last_init_date";
  const GIFT_PROP_AUTO_ADD = "_gift_auto_add";
  const GIFT_PROP_RULE_ID = "_gift_rule_id";
  const GIFT_PROP_TYPE = "_gift_type";
  const RULE_TYPE_BUY_X_GET_Y = "buy_x_get_y";
  const RULE_TYPE_MINIMUM_SPEND = "minimum_spend";

  let isSyncing = false;
  let syncTimer = null;
  let pendingSync = false;
  let pendingSeedCart = null;
  /** 访客 IP 推断国家，来自 browsing_context_suggestions.json */
  let detectedCountryCode = "";
  let detectedCountryPromise = null;
  /** 赠品变体解析缓存，避免重复打 /products/{handle}.js */
  const giftVariantCache = new Map();

  /** 获取全部规则 */
  function getRules(config) {
    return config?.rules || [];
  }

  /** 规则类型，缺省视为买 A 送 B */
  function getRuleType(rule) {
    return rule?.type === RULE_TYPE_MINIMUM_SPEND ? RULE_TYPE_MINIMUM_SPEND : RULE_TYPE_BUY_X_GET_Y;
  }

  /** 规则唯一标识（写入 line properties） */
  function getRuleId(rule) {
    if (rule?.id != null && rule.id !== "") return String(rule.id);
    if (rule?.handle != null && rule.handle !== "") return String(rule.handle);
    return "";
  }

  /** 是否为自动加购的赠品行 */
  function isAutoGiftLine(item) {
    const properties = item?.properties || {};
    const value = properties[GIFT_PROP_AUTO_ADD];
    return value === "1" || value === 1 || value === true || value === "true";
  }

  /** 行实付是否为 0（自动折扣后的免费赠品孤儿行） */
  function isFreeLine(item) {
    const finalLine = Number(item?.final_line_price);
    if (Number.isFinite(finalLine)) return finalLine === 0;

    const linePrice = Number(item?.line_price);
    if (Number.isFinite(linePrice)) return linePrice === 0;

    return Number(item?.price) === 0;
  }

  /** 构建自动加购行属性 */
  function buildGiftProperties(rule) {
    return {
      [GIFT_PROP_AUTO_ADD]: "1",
      [GIFT_PROP_RULE_ID]: getRuleId(rule),
      [GIFT_PROP_TYPE]: getRuleType(rule),
    };
  }

  /** 获取所有规则中的赠品 product ID 集合（用于排除主产品/低消统计） */
  function getAllGiftProductIdSet(config) {
    const giftIds = new Set();

    getRules(config).forEach((rule) => {
      (rule.giftProducts || []).forEach((giftProduct) => {
        giftIds.add(Number(giftProduct.id));
      });
    });

    return giftIds;
  }

  /** 获取购物车数据 */
  async function fetchCart() {
    const response = await fetch("/cart.js");
    if (!response.ok) throw new Error("Failed to fetch cart");
    return response.json();
  }

  /** 获取当前展示货币相对店铺货币的汇率 */
  function getCurrencyRate() {
    if (typeof Shopify !== "undefined" && Shopify.currency && Shopify.currency.rate) {
      const rate = parseFloat(Shopify.currency.rate);
      return Number.isFinite(rate) && rate > 0 ? rate : 1;
    }

    return 1;
  }

  /** 将店铺货币整数金额换算为当前展示货币的分值阈值 */
  function getMinimumAmountThresholdCents(minimumAmount) {
    const amount = Number(minimumAmount) || 0;
    if (amount <= 0) return 0;

    return amount * 100 * getCurrencyRate();
  }

  /** 从 Liquid 注入的配置中获取赠品变体（available 即可，支持继续售卖） */
  function getGiftVariant(giftProduct) {
    const variant = giftProduct?.variant;
    if (!variant || variant.available === false) return null;
    if (!variant.id) return null;
    return variant;
  }

  /** 页面配置无可用变体时，实时拉取产品 JSON 兜底（带缓存） */
  async function resolveGiftVariant(giftProduct) {
    const embedded = getGiftVariant(giftProduct);
    if (embedded) return embedded;

    const handle = giftProduct?.handle;
    if (!handle) return null;

    if (giftVariantCache.has(handle)) {
      return giftVariantCache.get(handle);
    }

    try {
      const response = await fetch(`/products/${encodeURIComponent(handle)}.js`);
      if (!response.ok) {
        giftVariantCache.set(handle, null);
        return null;
      }
      const product = await response.json();
      const variant = (product.variants || []).find((item) => item && item.available);
      if (!variant) {
        giftVariantCache.set(handle, null);
        return null;
      }

      const resolved = {
        id: variant.id,
        available: variant.available,
        inventoryManagement: variant.inventory_management || null,
        inventoryQuantity: variant.inventory_quantity,
      };
      giftVariantCache.set(handle, resolved);
      return resolved;
    } catch (error) {
      console.warn("[gift-auto-add] 拉取赠品变体失败:", handle, error);
      giftVariantCache.set(handle, null);
      return null;
    }
  }

  /** 行金额（分，当前货币） */
  function getLinePriceCents(item) {
    return Number(
      item.final_line_price ?? item.line_price ?? Number(item.price) * Number(item.quantity)
    );
  }

  /** 计算某条规则下 primary_products 的金额总和（分，当前货币，不含赠品产品 ID） */
  function getPrimaryProductsSubtotal(cart, rule, allGiftIds) {
    const primaryIdSet = new Set((rule.primaryProductIds || []).map(Number));

    return cart.items.reduce((total, item) => {
      if (allGiftIds.has(Number(item.product_id))) return total;
      if (!primaryIdSet.has(Number(item.product_id))) return total;

      return total + getLinePriceCents(item);
    }, 0);
  }

  /** 全车非赠品小计（排除全部 gift product ID 与自动赠品行；单位与 cart.js 一致，一般为分） */
  function getNonGiftCartSubtotal(cart, allGiftIds) {
    let sum = cart.items.reduce((total, item) => {
      if (allGiftIds.has(Number(item.product_id))) return total;
      if (isAutoGiftLine(item)) return total;

      return total + getLinePriceCents(item);
    }, 0);

    // 若行金额像「主货币单位」而 items_subtotal_price 像「分」，则换算到分
    const cartSubtotal = Number(cart.items_subtotal_price);
    if (
      Number.isFinite(cartSubtotal) &&
      cartSubtotal > 0 &&
      sum > 0 &&
      Math.abs(sum * 100 - cartSubtotal) / cartSubtotal < 0.15
    ) {
      sum *= 100;
    }

    return sum;
  }

  /** 购物车是否包含 minimum_bypass_products 中的产品（优先级最高） */
  function hasBypassProductInCart(cart, rule, allGiftIds) {
    const bypassIds = rule.minimumBypassProductIds || [];
    if (!bypassIds.length) return false;

    const bypassSet = new Set(bypassIds.map(Number));
    return cart.items.some(
      (item) => !allGiftIds.has(Number(item.product_id)) && bypassSet.has(Number(item.product_id))
    );
  }

  /** 计算某条规则下主产品总数量（含 bypass 产品，不含赠品产品 ID） */
  function getMainProductQuantityForRule(cart, rule, allGiftIds) {
    const primaryIdSet = new Set((rule.primaryProductIds || []).map(Number));
    const bypassIdSet = new Set((rule.minimumBypassProductIds || []).map(Number));

    return cart.items.reduce((total, item) => {
      if (allGiftIds.has(Number(item.product_id))) return total;

      const productId = Number(item.product_id);
      if (primaryIdSet.has(productId) || bypassIdSet.has(productId)) {
        return total + item.quantity;
      }

      return total;
    }, 0);
  }

  /** 买 A 送 B：按主产品数量计算目标赠品数量 */
  function getTargetGiftQuantity(mainProductQty, giftQuantityLimit) {
    if (mainProductQty <= 0) return 0;

    if (giftQuantityLimit > 0) {
      return Math.min(mainProductQty, giftQuantityLimit);
    }

    return mainProductQty;
  }

  /** 按规则类型计算目标赠品数量 */
  function getTargetGiftQuantityForRule(cart, rule, allGiftIds) {
    const limit = Number(rule.giftQuantityLimit) || 0;

    if (getRuleType(rule) === RULE_TYPE_MINIMUM_SPEND) {
      return limit > 0 ? limit : 1;
    }

    const mainProductQty = getMainProductQuantityForRule(cart, rule, allGiftIds);
    return getTargetGiftQuantity(mainProductQty, limit);
  }

  /** 根据变体库存限制最终加购数量（available 但库存 0/继续售卖时不拦截） */
  function capQuantityByInventory(quantity, variant) {
    if (quantity <= 0 || !variant) return 0;

    if (variant.inventoryManagement) {
      const inventoryQty = Number(variant.inventoryQuantity);
      if (Number.isFinite(inventoryQty) && inventoryQty > 0) {
        return Math.min(quantity, inventoryQty);
      }
    }

    return quantity;
  }

  /** 买 A 送 B 资格 */
  function isBuyXGetYQualified(cart, rule, allGiftIds) {
    if (hasBypassProductInCart(cart, rule, allGiftIds)) {
      return true;
    }

    if (!rule.primaryProductIds?.length) return false;

    const primaryIdSet = new Set(rule.primaryProductIds.map(Number));
    const hasPrimaryProduct = cart.items.some(
      (item) => !allGiftIds.has(Number(item.product_id)) && primaryIdSet.has(Number(item.product_id))
    );

    if (!hasPrimaryProduct) return false;

    const thresholdCents = getMinimumAmountThresholdCents(rule.minimumAmount);
    const primarySubtotal = getPrimaryProductsSubtotal(cart, rule, allGiftIds);

    if (thresholdCents > 0 && primarySubtotal <= thresholdCents) {
      return false;
    }

    return true;
  }

  /** 满低消送 C 资格（须严格大于门槛） */
  function isMinimumSpendQualified(cart, rule, allGiftIds) {
    const thresholdCents = getMinimumAmountThresholdCents(rule.minimumAmount);
    if (thresholdCents <= 0) return false;

    return getNonGiftCartSubtotal(cart, allGiftIds) > thresholdCents;
  }

  /** 将国家/地区代码规范为大写数组，支持逗号字符串或数组 */
  function normalizeCountryCodes(value) {
    if (value == null || value === "") return [];

    const rawList = Array.isArray(value) ? value : String(value).split(",");

    return rawList
      .map((code) => String(code).trim().toUpperCase())
      .filter(Boolean);
  }

  /** all / include / exclude。未写模式但有旧白名单时视为 include。 */
  function getCountryRuleMode(rule) {
    const raw = String(rule?.countryRuleMode ?? rule?.country_rule_mode ?? "")
      .trim()
      .toLowerCase();
    if (raw === "include" || raw === "exclude" || raw === "all") return raw;

    const legacy = normalizeCountryCodes(rule?.allowedCountries ?? rule?.allowed_countries);
    return legacy.length ? "include" : "all";
  }

  function getCountryCodes(rule) {
    const codes = normalizeCountryCodes(rule?.countryCodes ?? rule?.country_codes);
    if (codes.length) return codes;
    return normalizeCountryCodes(rule?.allowedCountries ?? rule?.allowed_countries);
  }

  function ruleUsesCountryLimit(rule) {
    const mode = getCountryRuleMode(rule);
    if (mode !== "include" && mode !== "exclude") return false;
    return getCountryCodes(rule).length > 0;
  }

  /**
   * all 或不填国家码：不限制。
   * include：仅名单内国家可享受。exclude：名单内国家不可享受。
   * 国家来自访客 IP；接口失败时回退店铺当前国家。
   */
  function isCountryAllowed(rule) {
    const mode = getCountryRuleMode(rule);
    const codes = getCountryCodes(rule);
    if (mode !== "include" && mode !== "exclude") return true;
    if (!codes.length) return true;
    if (!detectedCountryCode) return false;

    const listed = codes.includes(detectedCountryCode);
    return mode === "exclude" ? !listed : listed;
  }

  /**
   * 用 Shopify 根据访客 IP 推断的国家 handle，例如 US。
   * 每次页面访问只请求一次，结果只留在内存里。
   */
  async function fetchDetectedCountry() {
    try {
      const root = window.Shopify?.routes?.root || "/";
      const response = await fetch(`${root}browsing_context_suggestions.json`, {
        credentials: "same-origin",
      });
      if (!response.ok) throw new Error("Failed to fetch browsing context");

      const data = await response.json();
      const countryCode = String(data?.detected_values?.country?.handle || "")
        .trim()
        .toUpperCase();
      if (countryCode) {
        console.log("访客 IP 推断国家:", countryCode);
        return countryCode;
      }
    } catch (error) {
      console.error("获取失败:", error);
    }

    return String(window.giftAutoAddConfig?.country || "")
      .trim()
      .toUpperCase();
  }

  function ensureDetectedCountry(config) {
    const needsCountry = getRules(config).some(ruleUsesCountryLimit);
    if (!needsCountry) return Promise.resolve("");

    if (!detectedCountryPromise) {
      detectedCountryPromise = fetchDetectedCountry().then((code) => {
        detectedCountryCode = code;
        return code;
      });
    }

    return detectedCountryPromise;
  }

  /** 判断某条规则是否满足赠送条件 */
  function isRuleQualified(cart, rule, allGiftIds) {
    if (!rule.giftProducts?.length) return false;
    if (!isCountryAllowed(rule)) return false;

    if (getRuleType(rule) === RULE_TYPE_MINIMUM_SPEND) {
      return isMinimumSpendQualified(cart, rule, allGiftIds);
    }

    return isBuyXGetYQualified(cart, rule, allGiftIds);
  }

  /** 某赠品产品的全部自动赠品行 */
  function findAllAutoGiftLines(cart, giftProductId) {
    return cart.items.filter(
      (item) => Number(item.product_id) === Number(giftProductId) && isAutoGiftLine(item)
    );
  }

  /**
   * 无赠品属性、但实付为 0 的同 SKU 行（历史残留 / 折扣应用加购）
   * 付费单独购买同 SKU（实付 > 0）不会被当作孤儿行
   */
  function findOrphanFreeGiftLines(cart, giftProductId) {
    return cart.items.filter(
      (item) =>
        Number(item.product_id) === Number(giftProductId) &&
        !isAutoGiftLine(item) &&
        isFreeLine(item)
    );
  }

  /** 将行加入移除计划 */
  function planRemoveLine(plan, item) {
    if (!item) return;
    if (item.key) plan.updates[item.key] = 0;
    else plan.fallbackChanges.push({ item, quantity: 0 });
  }

  /** 获取购物车行的变更标识 */
  function getCartItemChangePayload(cart, item, quantity) {
    if (item?.key) {
      return { id: item.key, quantity };
    }

    const lineIndex = cart.items.indexOf(item);
    if (lineIndex < 0) return null;

    return { line: lineIndex + 1, quantity };
  }

  /** 合并批量加购项（同一变体 + 同一规则属性数量累加） */
  function mergeItemsToAdd(itemsToAdd) {
    const quantityMap = new Map();

    itemsToAdd.forEach((item) => {
      const properties = item.properties || {};
      const key = [
        Number(item.id),
        properties[GIFT_PROP_RULE_ID] || "",
        properties[GIFT_PROP_TYPE] || "",
      ].join("|");

      const existing = quantityMap.get(key);
      if (existing) {
        existing.quantity += Number(item.quantity);
      } else {
        quantityMap.set(key, {
          id: Number(item.id),
          quantity: Number(item.quantity),
          properties,
        });
      }
    });

    return Array.from(quantityMap.values());
  }

  /**
   * 汇总所有合格规则下「每个赠品 SKU」的目标数量。
   * 同一赠品被多条规则命中时取 max，避免加出两行。
   * 变体解析并行，减少串行等待。
   */
  async function buildDesiredGiftMap(cart, config, allGiftIds) {
    const desired = new Map();
    const qualifiedRules = getRules(config).filter((rule) => isRuleQualified(cart, rule, allGiftIds));

    const resolvedEntries = (
      await Promise.all(
        qualifiedRules.flatMap((rule) => {
          const targetQuantity = getTargetGiftQuantityForRule(cart, rule, allGiftIds);
          const giftProperties = buildGiftProperties(rule);

          return (rule.giftProducts || []).map(async (giftProduct) => {
            const productId = Number(giftProduct.id);
            if (!Number.isFinite(productId)) return null;

            const variant = await resolveGiftVariant(giftProduct);
            const finalQuantity = variant ? capQuantityByInventory(targetQuantity, variant) : 0;
            if (!variant || finalQuantity <= 0) {
              console.warn("[gift-auto-add] 赠品无可用变体，跳过加购:", giftProduct?.handle || productId);
              return null;
            }

            return {
              productId,
              quantity: finalQuantity,
              variantId: Number(variant.id),
              properties: giftProperties,
            };
          });
        })
      )
    ).filter(Boolean);

    resolvedEntries.forEach((entry) => {
      const current = desired.get(entry.productId);
      if (!current || entry.quantity > current.quantity) {
        desired.set(entry.productId, {
          quantity: entry.quantity,
          variantId: entry.variantId,
          properties: entry.properties,
        });
      }
    });

    return desired;
  }

  /**
   * 按赠品 SKU 同步购物车：
   * - 合格：只保留一行自动赠品并调到目标数量
   * - 不合格：移除全部自动赠品行 + 实付为 0 的孤儿赠品行
   */
  function syncGiftProductLines(cart, giftProductId, desired, plan) {
    const autoLines = findAllAutoGiftLines(cart, giftProductId);
    const orphanFreeLines = findOrphanFreeGiftLines(cart, giftProductId);
    const targetQuantity = desired?.quantity || 0;
    const targetVariantId = desired?.variantId;

    if (targetQuantity <= 0) {
      autoLines.forEach((item) => planRemoveLine(plan, item));
      orphanFreeLines.forEach((item) => planRemoveLine(plan, item));
      return;
    }

    // 未达标残留的免费孤儿行先清掉，避免与自动赠品重复
    orphanFreeLines.forEach((item) => planRemoveLine(plan, item));

    const matchingAutoLines = targetVariantId
      ? autoLines.filter((item) => Number(item.variant_id) === Number(targetVariantId))
      : autoLines;
    const keepLine = matchingAutoLines[0] || null;

    autoLines.forEach((item) => {
      if (keepLine && item.key && item.key === keepLine.key) return;
      if (keepLine && item === keepLine) return;
      planRemoveLine(plan, item);
    });

    if (keepLine) {
      if (Number(keepLine.quantity) !== Number(targetQuantity)) {
        if (keepLine.key) plan.updates[keepLine.key] = targetQuantity;
        else plan.fallbackChanges.push({ item: keepLine, quantity: targetQuantity });
      }
      return;
    }

    plan.itemsToAdd.push({
      id: targetVariantId,
      quantity: targetQuantity,
      properties: desired.properties,
    });
  }

  /** 当前 Active 规则 id 集合（system id / handle） */
  function getActiveRuleIdSet(config) {
    const ids = new Set();
    getRules(config).forEach((rule) => {
      const id = getRuleId(rule);
      if (id) ids.add(id);
    });
    return ids;
  }

  /**
   * 移除「活动已关闭 / 规则不在配置中」的自动赠品行。
   * Metaobject Draft 后前台拿不到规则，旧行会卡在购物车且无删除按钮。
   */
  function planRemoveStaleAutoGiftLines(cart, config, plan) {
    const activeRuleIds = getActiveRuleIdSet(config);

    cart.items.forEach((item) => {
      if (!isAutoGiftLine(item)) return;

      const ruleId = String(item.properties?.[GIFT_PROP_RULE_ID] || "");
      const ruleStillActive = ruleId && activeRuleIds.has(ruleId);
      if (ruleStillActive) return;

      planRemoveLine(plan, item);
    });
  }

  /** 根据全部规则构建合并后的同步计划（按赠品 SKU 去重/清理） */
  async function buildGiftSyncPlan(cart, config) {
    const plan = {
      updates: {},
      itemsToAdd: [],
      fallbackChanges: [],
    };

    const allGiftIds = getAllGiftProductIdSet(config);
    const desiredGiftMap = await buildDesiredGiftMap(cart, config, allGiftIds);

    // 先清掉已关闭活动留下的自动赠品行
    planRemoveStaleAutoGiftLines(cart, config, plan);

    allGiftIds.forEach((giftProductId) => {
      syncGiftProductLines(cart, giftProductId, desiredGiftMap.get(giftProductId) || null, plan);
    });

    plan.itemsToAdd = mergeItemsToAdd(plan.itemsToAdd);

    return plan;
  }

  /** 同步计划是否包含实际操作 */
  function planHasChanges(plan) {
    return (
      Object.keys(plan.updates).length > 0 ||
      plan.itemsToAdd.length > 0 ||
      plan.fallbackChanges.length > 0
    );
  }

  /** 批量更新购物车 */
  async function cartBatchUpdate(updates) {
    const response = await fetch(window.routes.cart_update_url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ updates }),
    });

    if (!response.ok) throw new Error("Failed to batch update cart");
    return response.json();
  }

  /** 批量加购赠品 */
  async function cartAddItems(items) {
    const response = await fetch(window.routes.cart_add_url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ items }),
    });

    if (!response.ok) {
      let detail = "";
      try {
        detail = await response.text();
      } catch (error) {
        detail = "";
      }
      throw new Error(`Failed to batch add gift items: ${response.status} ${detail}`);
    }
    return response.json();
  }

  /** 无 key 时的兜底单条变更 */
  async function changeCartItemQuantity(cart, item, quantity) {
    const payload = getCartItemChangePayload(cart, item, quantity);
    if (!payload) throw new Error("Cart line item not found");

    const response = await fetch(window.routes.cart_change_url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) throw new Error("Failed to change cart line");
    return response.json();
  }

  /** 执行同步计划（变更请求不附带 sections，先快速改购物车，再单独刷新 UI） */
  async function applyGiftSyncPlan(cart, plan) {
    let latestCart = cart;
    let needsRefetch = false;

    if (Object.keys(plan.updates).length > 0) {
      latestCart = await cartBatchUpdate(plan.updates);
    }

    for (const { item, quantity } of plan.fallbackChanges) {
      latestCart = await changeCartItemQuantity(latestCart, item, quantity);
    }

    if (plan.itemsToAdd.length > 0) {
      await cartAddItems(plan.itemsToAdd);
      needsRefetch = true;
    }

    return { cart: latestCart, needsRefetch };
  }

  /** 从 section HTML 中取出选择器对应节点的 innerHTML */
  function getSectionInnerHTML(sectionHtml, selector) {
    const doc = new DOMParser().parseFromString(sectionHtml, "text/html");
    if (!selector) {
      return doc.querySelector(".shopify-section")?.innerHTML || sectionHtml;
    }
    return doc.querySelector(selector)?.innerHTML || "";
  }

  /** 刷新购物车抽屉 UI */
  async function refreshCartDrawer() {
    const cartDrawer = document.querySelector("cart-drawer");
    if (!cartDrawer) return;

    const response = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`);
    if (!response.ok) return;

    const responseText = await response.text();
    const html = new DOMParser().parseFromString(responseText, "text/html");
    const selectors = ["cart-drawer-items", ".cart-drawer__footer", "cart-progress"];

    selectors.forEach((selector) => {
      const targetElement = document.querySelector(selector);
      const sourceElement = html.querySelector(selector);
      if (targetElement && sourceElement) {
        targetElement.replaceWith(sourceElement);
      }
    });

    const sourceDrawer = html.querySelector("cart-drawer");
    if (sourceDrawer) {
      cartDrawer.classList.toggle("is-empty", sourceDrawer.classList.contains("is-empty"));
    }
  }

  /** 是否为购物车页 */
  function isCartPage() {
    return (
      document.body.classList.contains("request-cart") ||
      /\/cart\/?$/.test(window.location.pathname)
    );
  }

  /** 刷新购物车页面 UI（商品行 + 底部合计，保证与服务器一致） */
  async function refreshCartPage(cartData) {
    const cartItems = document.querySelector("cart-items");
    if (!cartItems) return;

    const sectionId =
      document.getElementById("main-cart-items")?.dataset?.id || "main-cart-items";
    const footerEl = document.getElementById("main-cart-footer");
    const footerSectionId = footerEl?.dataset?.id;

    const sectionParams = [sectionId];
    if (footerSectionId) sectionParams.push(footerSectionId);

    const response = await fetch(
      `${window.routes.cart_url}?sections=${encodeURIComponent(sectionParams.join(","))}&sections_url=${encodeURIComponent(window.location.pathname)}&_=${Date.now()}`
    );

    if (!response.ok) {
      if (typeof cartItems.onCartUpdate === "function") {
        await Promise.resolve(cartItems.onCartUpdate());
      }
      return;
    }

    const parsedState = await response.json();
    const itemsHtml = parsedState[sectionId];
    if (itemsHtml) {
      const doc = new DOMParser().parseFromString(itemsHtml, "text/html");
      const sourceQty = doc.querySelector("cart-items");
      if (sourceQty) {
        cartItems.innerHTML = sourceQty.innerHTML;
      } else {
        const jsContents = document.querySelector("#main-cart-items .js-contents");
        // 限定在 #main-cart-items 内，避免误取 footer 等其他 .js-contents
        const nextHtml = getSectionInnerHTML(itemsHtml, "#main-cart-items .js-contents");
        if (jsContents && nextHtml !== "") {
          jsContents.innerHTML = nextHtml;
        }
      }
    }

    if (footerSectionId && parsedState[footerSectionId] && footerEl) {
      const footerHtml = getSectionInnerHTML(parsedState[footerSectionId], ".js-contents");
      const footerContents = footerEl.querySelector(".js-contents");
      if (footerContents && footerHtml !== "") {
        footerContents.innerHTML = footerHtml;
      }
    }

    const itemCount = Number(cartData?.item_count);
    if (Number.isFinite(itemCount)) {
      cartItems.classList.toggle("is-empty", itemCount === 0);
      if (footerEl) {
        footerEl.classList.toggle("is-empty", itemCount === 0);
      }
      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer) {
        cartDrawer.classList.toggle("is-empty", itemCount === 0);
      }
    }
  }

  /** 刷新购物车图标数量 */
  async function refreshCartIconBubble() {
    const response = await fetch(`${window.routes.cart_url}?section_id=cart-icon-bubble&_=${Date.now()}`);
    if (!response.ok) return;

    const responseText = await response.text();
    const html = new DOMParser().parseFromString(responseText, "text/html");
    const source = html.querySelector(".shopify-section");
    const target = document.getElementById("cart-icon-bubble");

    if (source && target) {
      target.innerHTML = source.innerHTML;
    }
  }

  /** 同步完成后刷新界面并广播 cartUpdate */
  async function refreshCartUI(cartData) {
    // 购物车页必须强制刷新，否则赠品已删除但表格仍残留
    const tasks = [];
    if (isCartPage() || document.querySelector("cart-items")) {
      tasks.push(refreshCartPage(cartData));
    }
    if (document.querySelector("cart-drawer")) {
      tasks.push(refreshCartDrawer());
    }
    tasks.push(refreshCartIconBubble());
    await Promise.all(tasks);

    // cart-items.innerHTML 替换后表单是新节点，需重新绑定
    if (isCartPage()) {
      bindCartFormSubmit();
    }

    if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined" && cartData) {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: GIFT_SOURCE,
        cartData,
      });
    }
  }

  /** 页面上的自动赠品行是否与 cart 数据不一致（需强制刷 UI） */
  function hasStaleAutoGiftDom(cart) {
    const domGiftCount = document.querySelectorAll(".cart-item--auto-gift").length;
    const cartGiftCount = (cart?.items || []).filter(isAutoGiftLine).length;
    return domGiftCount !== cartGiftCount;
  }

  /** 核心同步逻辑（最多两轮，清理竞态产生的重复/残留赠品） */
  async function syncGifts(seedCart) {
    const config = window.giftAutoAddConfig;
    if (!config) return;

    if (isSyncing) {
      pendingSync = true;
      if (seedCart?.items) pendingSeedCart = seedCart;
      return;
    }

    isSyncing = true;

    try {
      await ensureDetectedCountry(config);
      let cart = seedCart?.items ? seedCart : await fetchCart();
      let plan = await buildGiftSyncPlan(cart, config);
      let didMutate = false;

      if (planHasChanges(plan)) {
        const applied = await applyGiftSyncPlan(cart, plan);
        cart = applied.needsRefetch ? await fetchCart() : applied.cart;
        didMutate = true;

        plan = await buildGiftSyncPlan(cart, config);
        if (planHasChanges(plan)) {
          const second = await applyGiftSyncPlan(cart, plan);
          cart = second.needsRefetch ? await fetchCart() : second.cart;
        }
      }

      // 变更后必刷；即便本次无变更，DOM 仍有残留赠品时也要刷（例如上次刷 UI 失败）
      if (didMutate || hasStaleAutoGiftDom(cart)) {
        try {
          await refreshCartUI(cart);
        } catch (error) {
          console.error("[gift-auto-add] 刷新购物车 UI 失败:", error);
        }
      }
    } catch (error) {
      console.error("[gift-auto-add] 同步赠品失败:", error);
    } finally {
      isSyncing = false;

      if (pendingSync) {
        pendingSync = false;
        const nextSeed = pendingSeedCart;
        pendingSeedCart = null;
        scheduleSyncGifts(nextSeed);
      }
    }
  }

  /** 防抖触发同步（cartUpdate 已完成变更后再跑，短防抖即可） */
  function scheduleSyncGifts(seedCart) {
    if (seedCart?.items) pendingSeedCart = seedCart;
    if (syncTimer) window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(() => {
      const cart = pendingSeedCart;
      pendingSeedCart = null;
      syncGifts(cart);
    }, 80);
  }

  /** 同一自然日仅页面加载初始化一次 */
  function shouldRunDailyPageInit() {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const lastInitDate = window.localStorage.getItem(DAILY_INIT_STORAGE_KEY);

      if (lastInitDate === today) return false;

      window.localStorage.setItem(DAILY_INIT_STORAGE_KEY, today);
      return true;
    } catch (error) {
      console.warn("[gift-auto-add] localStorage 不可用，回退为每次页面加载同步:", error);
      return true;
    }
  }

  /**
   * 拦截 cart 页表单提交（Enter / Update 会整页 POST，无法触发 cartUpdate）
   * Checkout 先同步赠品再提交，避免未达标仍带着免费赠品进结账
   */
  function bindCartFormSubmit() {
    const form = document.getElementById("cart");
    if (!form || form.dataset.giftAutoAddBound === "true") return;

    form.dataset.giftAutoAddBound = "true";

    form.addEventListener("submit", async (event) => {
      const submitter = event.submitter;
      const isCheckout = Boolean(submitter && submitter.name === "checkout");

      if (isCheckout) {
        event.preventDefault();
        try {
          for (let i = 0; i < 80 && isSyncing; i += 1) {
            await new Promise((resolve) => setTimeout(resolve, 50));
          }
          if (syncTimer) {
            window.clearTimeout(syncTimer);
            syncTimer = null;
          }
          const seed = pendingSeedCart;
          pendingSeedCart = null;
          await syncGifts(seed);
        } catch (error) {
          console.error("[gift-auto-add] checkout 前同步赠品失败:", error);
        }

        const checkoutInput = document.createElement("input");
        checkoutInput.type = "hidden";
        checkoutInput.name = "checkout";
        checkoutInput.value = "";
        form.appendChild(checkoutInput);
        form.submit();
        return;
      }

      event.preventDefault();

      try {
        const cart = await fetchCart();
        const updates = {};

        form.querySelectorAll(".quantity__input").forEach((input) => {
          const line = parseInt(input.dataset.index, 10);
          const qty = parseInt(input.value, 10);
          if (!Number.isFinite(line) || !Number.isFinite(qty) || qty < 0) return;

          const item = cart.items[line - 1];
          if (!item || item.quantity === qty) return;

          if (item.key) updates[item.key] = qty;
        });

        if (Object.keys(updates).length > 0) {
          await cartBatchUpdate(updates);
        }

        await syncGifts();
      } catch (error) {
        console.error("[gift-auto-add] 购物车表单提交处理失败:", error);
        form.removeAttribute("data-gift-auto-add-bound");
        form.submit();
      }
    });
  }

  /** 初始化 */
  function initGiftAutoAdd() {
    const config = window.giftAutoAddConfig;
    if (!config) return;

    ensureDetectedCountry(config);

    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.source === GIFT_SOURCE) return;
        // 复用 cart.js 刚返回的 cartData，省掉一次 /cart.js
        scheduleSyncGifts(event?.cartData);
      });
    }

    if (isCartPage()) {
      bindCartFormSubmit();
      syncGifts();
      return;
    }

    // 无 Active 规则时仍同步一次，清掉已关闭活动残留的自动赠品行
    if (!getRules(config).length || shouldRunDailyPageInit()) {
      scheduleSyncGifts();
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initGiftAutoAdd);
  } else {
    initGiftAutoAdd();
  }
})();
